import DialogHeader from '@shared/ui/dialog/DialogHeader';
import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  List,
  ListItemButton,
  Pagination,
  Stack,
  Typography,
  Box,
  Avatar,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Product } from '@domain/product/models/Product';
import { DialogContent } from '@mui/material';
import { APP_CONFIG } from '@config/app.config';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import { useState } from 'react';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import EmptyState from '@shared/ui/common/EmptyState';
import { useProducts } from '../../hooks/useProducts';
import { ProductStatus } from '@domain/product/models/ProductFilter';

interface CombinePartProductSelectorProps {
  handleClose: () => void;
  /** Products to omit from the list (optional). Bundle flow leaves this empty so bundled products can be picked. */
  excludeProductIds?: Set<string>;
  /** Products already in the bundle table — shown in the list; selecting one moves it to the part product. */
  bundleProductIds?: Set<string>;
  handleFinish: (product: Product) => void;
}

export const CombinePartProductSelector: React.FC<CombinePartProductSelectorProps> = ({
  handleClose,
  excludeProductIds = new Set(),
  bundleProductIds = new Set(),
  handleFinish,
}) => {
  const { filterCriteria, page, setPage, limit } = useProductsFilter(50);
  const { products, isLoading, isFetching } = useProducts(
    { ...filterCriteria, page, limit },
    ProductStatus.ACTIVE
  );

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const toggleProduct = (product: Product) => {
    if (selectedProduct?.id === product.id) {
      setSelectedProduct(null);
    } else {
      setSelectedProduct(product);
    }
  };

  const productList =
    products?.status === 'success'
      ? products.data.filter((p) => !excludeProductIds.has(p.id))
      : [];

  const isInCurrentBundle = (id: string) => bundleProductIds.has(id);

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Select product to use as part in bundles
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        <Stack gap={0}>
          <Stack
            direction="row"
            position="sticky"
            top={0}
            justifyContent="flex-start"
            alignItems="center"
            px={4}
            py={2}
            spacing={2}
            borderBottom={1}
            borderColor="divider"
            bgcolor="background.paper"
            zIndex={99}
          >
            <ProductsFilter />
            <ActiveFilters />
          </Stack>

          {isLoading || isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : productList.length === 0 ? (
            <EmptyState
              title="No products found"
              description="No products found with the current filters"
            />
          ) : (
            <Stack gap={0}>
              <List sx={{ gap: 0, p: 0, width: '100%' }}>
                {productList.map((product) => (
                    <ListItemButton
                      key={product.id}
                      divider
                      onClick={() => toggleProduct(product)}
                      sx={{ p: 4 }}
                    >
                      <Stack direction="row" alignItems="center" flex={1} spacing={2}>
                        <Checkbox
                          checked={selectedProduct?.id === product.id}
                          onChange={() => toggleProduct(product)}
                        />

                        <Avatar
                          variant="rounded"
                          sx={{
                            border: 1,
                            borderColor: APP_CONFIG.ui.theme.colors.border,
                            width: 40,
                            height: 40,
                          }}
                          src={product.image}
                          alt={product.title}
                        />

                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="medium"
                            flexWrap="wrap"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {product.title || 'Untitled'}
                          </Typography>
                          {product.ean && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              EAN: {product.ean}
                            </Typography>
                          )}
                          {isInCurrentBundle(product.id) && (
                            <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                              In this bundle — selecting uses it as the part product and removes it from the bundle list
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </ListItemButton>
                ))}
              </List>

              {products?.status === 'success' && (
                <Stack p={2} direction="row" justifyContent="center" alignItems="center">
                  <Pagination
                    count={products.totalPages || 0}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    size="large"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="contained"
          disabled={!selectedProduct}
          onClick={() => selectedProduct && handleFinish(selectedProduct)}
        >
          Add Product
        </Button>
      </DialogActions>
    </>
  );
};
